import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight,
    FileText,
    Loader2,
    Lock,
    ScrollText,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { usePartnerAuth, formatApiError } from "@/context/PartnerAuthContext";

export default function PartnerAgreement() {
    const { status, partner, acceptAgreement, acceptAddendumA } = usePartnerAuth();
    const [checked, setChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addendumChecked, setAddendumChecked] = useState(false);
    const [addendumLoading, setAddendumLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const next = new URLSearchParams(location.search).get("next") || "/partner/dashboard";

    useEffect(() => {
        if (
            status === "authed" &&
            partner?.agreement_accepted &&
            partner?.addendum_a_accepted
        ) {
            navigate(next, { replace: true });
        }
    }, [status, partner, navigate, next]);

    if (status === "checking") {
        return (
            <div className="min-h-[70vh] bg-forest text-cream flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-gold" />
            </div>
        );
    }
    if (status === "guest") {
        return (
            <Navigate
                to={`/partner/auth?next=${encodeURIComponent("/partner/agreement")}`}
                replace
            />
        );
    }

    const sign = async () => {
        if (!checked) return;
        setLoading(true);
        try {
            await acceptAgreement();
            toast.success("Agreement signed · one more step: Addendum A");
            // Do not navigate — the Addendum A phase will now render.
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const signAddendum = async () => {
        if (!addendumChecked) return;
        setAddendumLoading(true);
        try {
            await acceptAddendumA();
            toast.success("Addendum A signed · welcome aboard");
            navigate(next, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setAddendumLoading(false);
        }
    };

    // Phase 2: main agreement signed, Addendum A pending.
    const addendumPhase =
        status === "authed" &&
        partner?.agreement_accepted &&
        !partner?.addendum_a_accepted;

    if (addendumPhase) {
        return (
            <div
                className="bg-forest text-cream min-h-screen py-16 px-5 md:px-10 relative overflow-hidden"
                data-testid="partner-addendum-a-page"
            >
                <div
                    className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
                    style={{ background: "var(--ig-gold)" }}
                    aria-hidden
                />
                <div className="grain absolute inset-0" />
                <div className="relative max-w-4xl mx-auto">
                    <div className="text-center">
                        <span className="divider-rule text-gold">
                            <Sparkles size={12} /> Supplement to Signed Agreement
                        </span>
                        <h1 className="font-serif text-5xl md:text-6xl text-cream mt-6 leading-[1.02]">
                            Addendum A ·{" "}
                            <span className="italic text-gold">
                                Affiliate Revenue
                            </span>
                        </h1>
                        <p className="mt-4 text-cream/70 max-w-xl mx-auto">
                            Your Platform Partner Agreement is on file. One
                            more supplement governs income from third-party
                            affiliate programs routed through your storefront.
                        </p>
                    </div>

                    <section
                        className="mt-12 bg-cream text-forest p-8 md:p-10 rounded-sm"
                        data-testid="partner-addendum-a-doc"
                    >
                        <header className="flex items-center gap-3">
                            <ScrollText size={20} className="text-gold" />
                            <h2 className="font-serif text-2xl md:text-3xl text-forest">
                                Addendum A — Affiliate Revenue Sharing
                            </h2>
                            <span className="ml-auto text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                Supplement 01
                            </span>
                        </header>

                        <div className="mt-6 space-y-5 text-brown leading-relaxed text-[0.95rem]">
                            <p>
                                This addendum supplements the Platform Partner
                                Agreement between{" "}
                                <strong className="text-forest">EIGOO Inc.</strong>{" "}
                                (&ldquo;Platform&rdquo;) and the Partner
                                (&ldquo;<strong className="text-forest">Ymaku K Robert</strong>&rdquo;)
                                dated{" "}
                                <strong className="text-forest">October 1, 2026</strong>.
                            </p>

                            <h3 className="font-serif text-xl text-forest pt-2">
                                1. Scope
                            </h3>
                            <p>
                                This addendum governs revenue generated through
                                affiliate program commissions — defined as
                                earnings from third-party programs (including
                                but not limited to{" "}
                                <strong className="text-forest">
                                    Amazon Associates, Awin, Impact, ShareASale,
                                    and Shopify Collabs
                                </strong>
                                ) accessed through the Partner&rsquo;s
                                storefront via EIGOO&rsquo;s tracking
                                infrastructure (<code className="text-forest font-mono text-xs">/go/</code>{" "}
                                routing, affiliate links, and attribution tags).
                            </p>

                            <h3 className="font-serif text-xl text-forest pt-2">
                                2. Revenue Split
                            </h3>
                            <p>
                                Affiliate commission revenue, after receipt
                                from the third-party program, shall be
                                distributed as follows:
                            </p>

                            <div className="bg-cream-warm border border-forest/10 rounded-sm p-6">
                                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                    Affiliate Commission · 70 / 20 / 10
                                </p>
                                <div
                                    className="mt-4 grid grid-cols-3 gap-2 md:gap-4"
                                    data-testid="partner-addendum-a-split"
                                >
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">70%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">
                                            Partner
                                        </p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">
                                            Ymaku K Robert
                                        </p>
                                    </div>
                                    <div className="text-center border-l border-r border-forest/10">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">20%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">
                                            EIGOO Inc.
                                        </p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">
                                            Platform &amp; tracking fee
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-gold">10%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">
                                            CyberKids
                                        </p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">
                                            Round-up
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <h3 className="font-serif text-xl text-forest pt-2">
                                3. Distribution
                            </h3>
                            <p>
                                Payments to the Partner shall be issued on the
                                same schedule as product revenue distributions,
                                as defined in the original Platform Partner
                                Agreement.
                            </p>

                            <h3 className="font-serif text-xl text-forest pt-2">
                                4. Transparency
                            </h3>
                            <p>
                                EIGOO Inc. shall provide the Partner with
                                affiliate earnings reports through the Partner
                                dashboard, showing commissions earned, split
                                applied, and net payout.
                            </p>

                            <h3 className="font-serif text-xl text-forest pt-2">
                                5. Governing Terms
                            </h3>
                            <p>
                                All other terms of the original Platform
                                Partner Agreement remain in full effect.
                            </p>

                            <div
                                className="mt-6 pt-6 border-t border-forest/10 grid sm:grid-cols-2 gap-6"
                                data-testid="partner-addendum-a-signatures"
                            >
                                <div>
                                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                        Counter-signatory · EIGOO Inc.
                                    </p>
                                    <p
                                        className="font-serif text-xl text-forest mt-2"
                                        data-testid="addendum-a-eigoo-signatory-name"
                                    >
                                        Dontae Kirk
                                    </p>
                                    <p
                                        className="text-xs text-brown mt-1"
                                        data-testid="addendum-a-eigoo-signatory-title"
                                    >
                                        Director, EIGOO Inc.
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                        Partner
                                    </p>
                                    <p className="font-serif text-xl text-forest mt-2">
                                        Ymaku K Robert
                                    </p>
                                    <p className="text-xs text-brown mt-1 font-mono">
                                        {partner?.email}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div
                        className="mt-10 bg-cream/5 border border-gold/40 p-8 rounded-sm backdrop-blur"
                        data-testid="partner-addendum-a-signature"
                    >
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={addendumChecked}
                                onChange={(e) =>
                                    setAddendumChecked(e.target.checked)
                                }
                                data-testid="partner-addendum-a-checkbox"
                                className="mt-1 w-5 h-5 accent-[var(--ig-gold)] shrink-0"
                            />
                            <span className="text-cream leading-relaxed">
                                I have read and agree to Addendum A — Affiliate
                                Revenue Sharing, including the 70% / 20% / 10%
                                split on affiliate commission revenue (Partner /
                                EIGOO Inc. / CyberKids Initiative). This
                                addendum supplements, and does not replace, my
                                signed Platform Partner Agreement.
                            </span>
                        </label>
                        <button
                            type="button"
                            onClick={signAddendum}
                            disabled={!addendumChecked || addendumLoading}
                            data-testid="partner-addendum-a-submit"
                            className="btn-pill btn-gold mt-8 w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {addendumLoading ? (
                                <>
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />{" "}
                                    Signing…
                                </>
                            ) : (
                                <>
                                    Accept Addendum A{" "}
                                    <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                        <p className="mt-4 text-[0.62rem] tracking-[0.28em] uppercase text-cream/50 text-center">
                            Timestamp recorded on acceptance · supplement to
                            signed MSA
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="bg-forest text-cream min-h-screen py-16 px-5 md:px-10 relative overflow-hidden"
            data-testid="partner-agreement-page"
        >
            <div
                className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
                style={{ background: "var(--ig-gold)" }}
                aria-hidden
            />
            <div className="grain absolute inset-0" />
            <div className="relative max-w-4xl mx-auto">
                <div className="text-center">
                    <span className="divider-rule text-gold">
                        <ShieldCheck size={12} /> Partner Onboarding · One-Time
                        Review
                    </span>
                    <h1 className="font-serif text-5xl md:text-6xl text-cream mt-6 leading-[1.02]">
                        Agreement &amp;{" "}
                        <span className="italic text-gold">NDA</span>
                    </h1>
                    <p className="mt-4 text-cream/70 max-w-xl mx-auto">
                        Please review both documents in full before signing.
                        Signed to the email{" "}
                        <span className="text-gold font-mono text-sm">
                            {partner?.email}
                        </span>
                    </p>
                </div>

                {/* NDA */}
                <section
                    className="mt-12 bg-cream text-forest p-8 md:p-10 rounded-sm"
                    data-testid="partner-agreement-nda"
                >
                    <header className="flex items-center gap-3">
                        <FileText size={20} className="text-gold" />
                        <h2 className="font-serif text-2xl md:text-3xl text-forest">
                            Partner Non-Disclosure Agreement
                        </h2>
                        <span className="ml-auto text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                            Document 01
                        </span>
                    </header>
                    <div className="mt-6 space-y-4 text-brown leading-relaxed text-[0.95rem]">
                        <p>
                            All confidential business information, brand
                            assets, product formulas, supplier relationships,
                            pricing strategies, and internal communications
                            relating to{" "}
                            <strong className="text-forest">
                                Island Gold Glow Soap Co.
                            </strong>{" "}
                            are the sole and proprietary property of{" "}
                            <strong className="text-forest">EIGOO Inc.</strong>{" "}
                            and <strong className="text-forest">Dontae Kirk (Director, EIGOO Inc.)</strong>.
                        </p>
                        <p>
                            By signing below, the Partner (hereafter{" "}
                            <em>“Ymaku”</em> or <em>“Creator”</em>) agrees
                            that she shall not — during the term of this
                            agreement or at any time thereafter — directly or
                            indirectly disclose, publish, reproduce, replicate,
                            reverse-engineer, resell, or otherwise distribute
                            any portion of the proprietary information to any
                            third party without the express written consent of
                            EIGOO Inc.
                        </p>
                        <p>
                            This obligation extends to all product recipes,
                            cold-process soap formulations, supplier contact
                            lists, Shopify/Stripe analytics, customer PII,
                            marketing playbooks, and the EIGOO Signal-Link
                            Engine source code and configuration.
                        </p>
                        <p>
                            Breach of this NDA shall entitle EIGOO Inc. to
                            immediate termination of the Creator
                            Agreement, full forfeiture of accrued revenue
                            shares, and such legal remedies as may be available
                            under the laws of the State of Hawaii.
                        </p>
                    </div>
                </section>

                {/* Creator Agreement */}
                <section
                    className="mt-8 bg-cream text-forest p-8 md:p-10 rounded-sm"
                    data-testid="partner-agreement-operator"
                >
                    <header className="flex items-center gap-3">
                        <ScrollText size={20} className="text-gold" />
                        <h2 className="font-serif text-2xl md:text-3xl text-forest">
                            Creator Agreement
                        </h2>
                        <span className="ml-auto text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                            Document 02
                        </span>
                    </header>

                    <div className="mt-6 space-y-5 text-brown leading-relaxed text-[0.95rem]">
                        <p>
                            This is a <strong>free-entry partnership</strong>.
                            No upfront capital is required from the Hull
                            Operator. In recognition of her operational labor
                            and goodwill, Ymaku receives the majority revenue
                            share from the first dollar.
                        </p>

                        <div className="bg-gold/15 border border-gold rounded-sm p-5" data-testid="partner-agreement-ownership-disclosure">
                            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-forest font-semibold">
                                Ownership · Not an Equity Arrangement
                            </p>
                            <p className="mt-3 text-forest text-sm leading-relaxed">
                                <strong>
                                    Island Gold Glow Soap Co. LLC is owned
                                    100% by Ymaku K. Robert as sole member.
                                </strong>{" "}
                                EIGOO Inc. and the CyberKids Initiative are
                                NOT members, shareholders, partners, or
                                equity holders in the LLC. The percentages
                                below represent{" "}
                                <strong>
                                    contractual revenue-share recipients
                                </strong>{" "}
                                only — commissions and philanthropic
                                royalties paid from NET revenue.
                            </p>
                            <p className="mt-3 text-forest text-xs leading-relaxed">
                                This structure preserves the Operator's
                                eligibility for minority-, woman-, and
                                Pacific-Islander-owned business grants and
                                certifications that require 51%+ ownership
                                by the qualifying individual.
                            </p>
                        </div>

                        <div className="bg-brown/10 border border-brown/30 rounded-sm p-5"
                            data-testid="partner-agreement-eigoo-prereq"
                        >
                            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-forest font-semibold">
                                EIGOO Entity Reinstatement · Prerequisite
                            </p>
                            <p className="mt-3 text-brown text-sm leading-relaxed">
                                EIGOO Inc. must be a legal entity{" "}
                                <strong className="text-forest">
                                    in good standing — formally reinstated
                                    with the Hawai'i DCCA
                                </strong>{" "}
                                — before it can act as counter-signatory on
                                this agreement or collect any revenue-share
                                commission under the lanes below.
                            </p>
                            <p className="mt-2 text-brown text-xs leading-relaxed">
                                Until EIGOO Inc. is reinstated, EIGOO's share
                                of each applicable lane accrues to a
                                pending-reinstatement reserve but is not
                                distributed. The Creator's share and the
                                CyberKids 10% philanthropic royalty flow
                                normally.
                            </p>
                        </div>

                        <div
                            className="bg-cream-warm border border-forest/10 rounded-sm p-6"
                            data-testid="partner-agreement-lanes"
                        >
                            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                Revenue Model · Four Lanes · NET Revenue Basis
                            </p>
                            <p className="mt-3 text-sm text-brown leading-relaxed">
                                The split applied to any given sale depends on
                                how the product was sourced and created.
                                Every lane uses the same NET-revenue basis.
                            </p>

                            {/* Lane 1 — Creator-owned IP */}
                            <div
                                className="mt-5 rounded-sm border border-forest/15 bg-cream p-5"
                                data-testid="partner-agreement-lane-1"
                            >
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                        Lane 1 · Creator's Own Products
                                    </p>
                                    <p className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70">
                                        Soaps · Songs · Royalties · Any IP the creator originated
                                    </p>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2 md:gap-4">
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">
                                            100%
                                        </p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">
                                            Creator
                                        </p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">
                                            Retained · 100% equity owner
                                        </p>
                                    </div>
                                    <div className="text-center border-l border-forest/10">
                                        <p className="font-serif text-[1.1rem] leading-tight md:text-xl text-forest">
                                            Flat setup fee +<br />monthly subscription
                                        </p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">
                                            EIGOO Inc.
                                        </p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">
                                            Services fee · no revenue split
                                        </p>
                                    </div>
                                </div>
                                <p className="mt-3 text-xs text-brown leading-relaxed">
                                    Creator keeps 100% of net revenue on their
                                    own products. EIGOO earns via platform
                                    setup fee and monthly subscription only.
                                </p>
                            </div>

                            {/* Lane 2 — Affiliate */}
                            <div
                                className="mt-4 rounded-sm border border-forest/15 bg-cream p-5"
                                data-testid="partner-agreement-lane-2"
                            >
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                        Lane 2 · Affiliate Products
                                    </p>
                                    <p className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70">
                                        EIGOO sources · Creator promotes
                                    </p>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2 md:gap-4">
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">70%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">Creator</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Of net commission</p>
                                    </div>
                                    <div className="text-center border-l border-r border-forest/10">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">20%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">EIGOO Inc.</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Platform &amp; tracking</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-gold">10%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">CyberKids</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Philanthropic royalty</p>
                                    </div>
                                </div>
                            </div>

                            {/* Lane 3 — EIGOO Dropship */}
                            <div
                                className="mt-4 rounded-sm border border-forest/15 bg-cream p-5"
                                data-testid="partner-agreement-lane-3"
                            >
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                        Lane 3 · EIGOO Dropship Products
                                    </p>
                                    <p className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70">
                                        EIGOO supplies finished inventory · Creator sells
                                    </p>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2 md:gap-4">
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">50%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">Creator</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Of net revenue</p>
                                    </div>
                                    <div className="text-center border-l border-r border-forest/10">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">40%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">EIGOO Inc.</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Sourcing &amp; fulfillment</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-gold">10%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">CyberKids</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Philanthropic royalty</p>
                                    </div>
                                </div>
                            </div>

                            {/* Lane 4 — Co-created */}
                            <div
                                className="mt-4 rounded-sm border border-forest/15 bg-cream p-5"
                                data-testid="partner-agreement-lane-4"
                            >
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                        Lane 4 · Co-Created Products
                                    </p>
                                    <p className="text-[0.6rem] tracking-[0.25em] uppercase text-brown/70">
                                        Both parties contribute creative IP
                                    </p>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2 md:gap-4">
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">45%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">Creator</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Of net revenue</p>
                                    </div>
                                    <div className="text-center border-l border-r border-forest/10">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-forest">45%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">EIGOO Inc.</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Co-IP contribution</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="font-serif text-[2.5rem] leading-none md:text-5xl md:leading-tight text-gold">10%</p>
                                        <p className="mt-2 text-[0.65rem] md:text-xs tracking-[0.2em] uppercase text-brown">CyberKids</p>
                                        <p className="mt-1 text-[0.6rem] md:text-[0.65rem] text-brown/70">Philanthropic royalty</p>
                                    </div>
                                </div>
                            </div>

                            <p className="mt-5 text-sm text-brown leading-relaxed">
                                All percentages apply to{" "}
                                <strong className="text-forest">NET revenue</strong>{" "}
                                — gross revenue minus (a) payment-processor
                                card fees and (b) cost of goods sold.
                                Reconciled monthly via ACH on the 15th of the
                                following month. All amounts are paid as
                                commissions / royalties — not as dividends or
                                distributions.
                            </p>
                        </div>

                        {/* Advance & Recoup */}
                        <div
                            className="bg-cream-warm border border-forest/10 rounded-sm p-6"
                            data-testid="partner-agreement-advance-recoup"
                        >
                            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                Advance &amp; Recoup
                            </p>
                            <h3 className="font-serif text-2xl text-forest mt-3">
                                If EIGOO advances capital
                            </h3>
                            <ul className="mt-4 space-y-3 text-sm text-brown leading-relaxed list-disc pl-5">
                                <li>
                                    <strong className="text-forest">Advance.</strong>{" "}
                                    If EIGOO advances capital on the Creator's
                                    behalf — for example, supply costs, build
                                    costs, production tooling, or other agreed
                                    pre-financing — the Creator does{" "}
                                    <strong className="text-forest">not</strong>{" "}
                                    retain 100% on Lane 1 products until the
                                    advance balance is fully repaid.
                                </li>
                                <li>
                                    <strong className="text-forest">Recoupment.</strong>{" "}
                                    A defined percentage of each sale — the
                                    recoup rate — is applied toward the
                                    outstanding advance balance until the
                                    balance is cleared. The specific recoup
                                    rate is set per advance in a signed
                                    schedule attached to this agreement.
                                </li>
                                <li>
                                    <strong className="text-forest">
                                        Return to 100% after recoup.
                                    </strong>{" "}
                                    Once the advance is fully recouped, the
                                    Creator resumes retaining 100% of net
                                    revenue on Lane 1 products going forward.
                                </li>
                                <li>
                                    <strong className="text-forest">Exit obligation.</strong>{" "}
                                    If the Creator exits — through sale,
                                    merger, dissolution, or voluntary
                                    termination of this agreement — before the
                                    advance is fully recouped, the outstanding
                                    balance becomes{" "}
                                    <strong className="text-forest">
                                        immediately due and payable
                                    </strong>{" "}
                                    at exit.
                                </li>
                            </ul>
                            <p className="mt-4 text-xs text-brown italic leading-relaxed">
                                Recoupment is a repayment mechanism, not an
                                ownership transfer. EIGOO's advance never
                                converts into equity in the Creator's LLC.
                            </p>
                        </div>

                        <h3
                            className="font-serif text-xl text-forest pt-2"
                            data-testid="partner-agreement-exit-heading"
                        >
                            Exit Clause · Sale, Merger, or Dissolution
                        </h3>
                        <p>
                            If the Operator sells the business, enters a
                            merger or acquisition, or dissolves the LLC, this
                            revenue-share arrangement is governed by a
                            90-day written notice and one of three options,
                            at the Operator's election:
                        </p>
                        <ol
                            className="list-decimal pl-6 space-y-2 text-sm"
                            data-testid="partner-agreement-exit-options"
                        >
                            <li>
                                <strong className="text-forest">
                                    Buyer assumes the revenue share
                                </strong>{" "}
                                — the acquiring party assumes EIGOO's 40%
                                commission and CyberKids' 10% royalty under
                                identical terms for the remainder of the
                                active agreement.
                            </li>
                            <li>
                                <strong className="text-forest">
                                    One-time buyout
                                </strong>{" "}
                                — the revenue-share obligations may be
                                terminated by a one-time cash buyout equal to
                                the trailing twenty-four (24) months of
                                combined EIGOO + CyberKids distributions,
                                payable at closing.
                            </li>
                            <li>
                                <strong className="text-forest">
                                    Orderly wind-down
                                </strong>{" "}
                                — if the business is dissolved rather than
                                sold, the revenue share terminates upon the
                                LLC's final tax return. No clawback of
                                previously distributed amounts.
                            </li>
                        </ol>
                        <p className="text-sm text-brown italic">
                            The Non-Disclosure Agreement and all
                            confidentiality obligations survive any exit
                            indefinitely.
                        </p>

                        <h3 className="font-serif text-xl text-forest pt-2">
                            Affiliate Commission Ladder · Referenced by this MSA
                        </h3>
                        <p className="text-sm">
                            Partner may also earn additional income by
                            referring other creators to the EIGOO Creator Hub
                            under the EIGOO Affiliate Program. The entry-tier
                            rate is{" "}
                            <strong className="text-forest">
                                20% recurring commission
                            </strong>{" "}
                            on referred creator subscriptions. Advancement to
                            higher commission tiers is available per EIGOO
                            infrastructure policy (performance, volume, and
                            tenure); exact rates for higher tiers are set at
                            promotion by the Director. The current Affiliate
                            Agreement is a separate document reviewed and
                            signed from the Partner dashboard.
                        </p>

                        <h3 className="font-serif text-xl text-forest pt-2">
                            Operator Security Requirements
                        </h3>
                        <p>
                            To protect the integrity of the Hull, the Partner
                            agrees to maintain the following safeguards on all
                            devices used to access EIGOO systems:
                        </p>
                        <ul className="list-none space-y-3">
                            <li className="flex items-start gap-3">
                                <Lock
                                    size={14}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <span>
                                    <strong className="text-forest">
                                        Two-Factor Authentication (2FA):
                                    </strong>{" "}
                                    enabled on this account, email, and any
                                    linked Stripe/Shopify consoles.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Lock
                                    size={14}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <span>
                                    <strong className="text-forest">
                                        VPN:
                                    </strong>{" "}
                                    all administrative access routed through an
                                    approved VPN (ProtonVPN or Mullvad) when on
                                    public or shared networks.
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Lock
                                    size={14}
                                    className="text-gold mt-1 shrink-0"
                                />
                                <span>
                                    <strong className="text-forest">
                                        Strong Password Policy:
                                    </strong>{" "}
                                    unique passphrase of 16+ characters per
                                    system, stored in a reputable password
                                    manager (1Password / Bitwarden).
                                </span>
                            </li>
                        </ul>

                        <p className="pt-2 text-sm">
                            This agreement remains in effect until terminated
                            in writing by either party with 30 days&apos;
                            notice. Phase transitions, revenue adjustments, and
                            scope expansions will be documented as signed
                            amendments.
                        </p>

                        <div
                            className="mt-6 pt-6 border-t border-forest/10 grid sm:grid-cols-2 gap-6"
                            data-testid="partner-agreement-signatures"
                        >
                            <div>
                                <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                    Counter-signatory · EIGOO Inc.
                                </p>
                                <p
                                    className="font-serif text-xl text-forest mt-2"
                                    data-testid="eigoo-signatory-name"
                                >
                                    Dontae Kirk
                                </p>
                                <p
                                    className="text-xs text-brown mt-1"
                                    data-testid="eigoo-signatory-title"
                                >
                                    Director, EIGOO Inc.
                                </p>
                            </div>
                            <div>
                                <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown">
                                    Partner · Creator
                                </p>
                                <p className="font-serif text-xl text-forest mt-2">
                                    {partner?.email}
                                </p>
                                <p className="text-xs text-brown mt-1">
                                    Signature captured on acceptance below
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Signature */}
                <div
                    className="mt-10 bg-cream/5 border border-gold/40 p-8 rounded-sm backdrop-blur"
                    data-testid="partner-agreement-signature"
                >
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            data-testid="partner-agreement-checkbox"
                            className="mt-1 w-5 h-5 accent-[var(--ig-gold)] shrink-0"
                        />
                        <span className="text-cream leading-relaxed">
                            I have read and agree to the Partner Non-Disclosure
                            Agreement and the Creator Agreement above,
                            including the four-lane revenue model (Lane 1 ·
                            own products 100% to Creator; Lane 2 · affiliate
                            70/20/10; Lane 3 · EIGOO dropship 50/40/10;
                            Lane 4 · co-created 45/45/10), the Advance &amp;
                            Recoup terms, the acknowledgment that I am the
                            100% equity owner of my LLC, and the 2FA, VPN,
                            and strong-password security requirements.
                        </span>
                    </label>
                    <button
                        type="button"
                        onClick={sign}
                        disabled={!checked || loading}
                        data-testid="partner-agreement-submit"
                        className="btn-pill btn-gold mt-8 w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />{" "}
                                Signing…
                            </>
                        ) : (
                            <>
                                Sign &amp; Continue <ArrowRight size={14} />
                            </>
                        )}
                    </button>
                    <p className="mt-4 text-[0.62rem] tracking-[0.28em] uppercase text-cream/50 text-center">
                        Timestamp recorded on acceptance · one-time only
                    </p>
                </div>
            </div>
        </div>
    );
}
